/**
 * A tela de espera da confirmação do e-mail (docs/spec/email.md): a conta nova fica aqui, no lugar do
 * app, até abrir o link que chegou no e-mail. Ela pergunta ao servidor de tempos em tempos e quando a
 * janela volta ao foco: o link aberto noutra aba, noutro navegador ou no celular libera esta também.
 *
 * A moldura é a da tela de entrada (disconnected.css); o conteúdo vai no fluxo, e não nas posições do
 * formulário de referência, e a caixa cresce com o texto.
 */
import { useEffect, useState, type KeyboardEvent } from 'react';
import { useLanguage, useT } from '../i18n/index.js';
import { EMAIL_CONFIRMATION_LIMITS } from '../shared.js';
import { useApp } from '../store.js';
import { Button, ErrorLabel, TextEdit } from '../ui/controls.js';
import { emphasize } from '../ui/emphasize.js';
import { Icon, RpgDeskMark } from '../ui/icons.js';
import { LanguageButton } from './DisconnectedState.js';
import './disconnected.css';

/** De quanto em quanto tempo a tela pergunta se a confirmação chegou. */
const CHECK_MS = 5000;
const LINK_HOURS = Math.round(EMAIL_CONFIRMATION_LIMITS.linkMs / (60 * 60 * 1000));

export function ConfirmEmailState() {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const pending = useApp((s) => s.emailConfirmation);
  const refresh = useApp((s) => s.refreshEmailConfirmation);
  const resend = useApp((s) => s.resendConfirmation);
  const changeEmail = useApp((s) => s.changePendingEmail);
  const logout = useApp((s) => s.logout);
  const [now, setNow] = useState(() => Date.now());
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resent, setResent] = useState(false);

  // a confirmação chega por fora (o link noutra aba ou noutro aparelho): pergunta de tempos em tempos
  useEffect(() => {
    const check = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const timer = window.setInterval(check, CHECK_MS);
    window.addEventListener('focus', check);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
    };
  }, [refresh]);

  // a contagem até poder pedir outro e-mail
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  if (!pending) return null;
  const wait = Math.max(0, pending.resendAt - now);
  const seconds = Math.ceil(wait / 1000);
  const countdown = t('confirmEmail.resendIn', { time: `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` });
  const deleteDate = new Date(pending.deleteAt).toLocaleDateString(language, { day: 'numeric', month: 'long' });

  async function doResend() {
    setBusy(true);
    setError('');
    setResent(false);
    const problem = await resend();
    setBusy(false);
    setNow(Date.now());
    if (problem) setError(problem);
    else setResent(true);
  }

  async function doChange() {
    const clean = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setError(t('register.error.invalid.email'));
      return;
    }
    setBusy(true);
    setError('');
    const problem = await changeEmail(clean);
    setBusy(false);
    setNow(Date.now());
    if (problem) {
      setError(problem);
      return;
    }
    setEditing(false);
    setResent(true);
  }

  const onEnter = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void doChange();
    }
  };

  return (
    <div className="rd-disconnected">
      <div className="rd-disconnected__floater rd-confirmEmail__floater">
        <div className="rd-disconnected__icon">
          <RpgDeskMark size={132} />
          <div className="rd-disconnected__brand">{t('app.name')}</div>
          <div className="rd-disconnected__tagline">{t('app.tagline')}</div>
        </div>

        <div className="rd-disconnected__client">
          <div className="rd-disconnected__top">
            <LanguageButton />
          </div>

          <div className="rd-disconnected__center">
            <div className="rd-confirmEmail">
              <span className="rd-confirmEmail__icon" aria-hidden="true">
                <Icon name="mail" size={26} />
              </span>
              <h1 className="rd-confirmEmail__title">{t('confirmEmail.title')}</h1>
              {editing ? (
                <>
                  <p className="rd-confirmEmail__text">{t('confirmEmail.change.text')}</p>
                  <TextEdit
                    icon="mail"
                    value={email}
                    onChange={setEmail}
                    placeholder={t('register.email')}
                    aria-label={t('register.email')}
                    autoComplete="email"
                    invalid={!!error}
                    autoFocusOnMount
                    onKeyDown={onEnter}
                  />
                  {error ? <ErrorLabel>{error}</ErrorLabel> : null}
                  <Button variant="action" busy={busy} disabled={wait > 0} onClick={() => void doChange()}>
                    {wait > 0 ? countdown : t('confirmEmail.change.submit')}
                  </Button>
                  <Button
                    variant="link"
                    onClick={() => {
                      setEditing(false);
                      setError('');
                    }}
                  >
                    {t('confirmEmail.change.cancel')}
                  </Button>
                </>
              ) : (
                <>
                  <p className="rd-confirmEmail__text">
                    {pending.sentAt === null
                      ? emphasize(t('confirmEmail.failed', { email: '\u0000' }), pending.email)
                      : emphasize(t('confirmEmail.sent', { email: '\u0000' }), pending.email)}
                  </p>
                  {pending.sentAt !== null ? <p className="rd-confirmEmail__text">{t('confirmEmail.open', { hours: LINK_HOURS })}</p> : null}
                  <Button variant="secondary" busy={busy} disabled={wait > 0} onClick={() => void doResend()}>
                    {wait > 0 ? countdown : t('confirmEmail.resend')}
                  </Button>
                  {error ? (
                    <ErrorLabel>{error}</ErrorLabel>
                  ) : resent ? (
                    <p className="rd-confirmEmail__status" role="status">
                      {t('confirmEmail.resent')}
                    </p>
                  ) : null}
                  <Button
                    variant="strongLink"
                    onClick={() => {
                      setEditing(true);
                      setEmail(pending.email);
                      setError('');
                      setResent(false);
                    }}
                  >
                    {t('confirmEmail.wrong')}
                  </Button>
                  <p className="rd-confirmEmail__note">{t('confirmEmail.deleteAt', { date: deleteDate })}</p>
                </>
              )}
            </div>
          </div>

          <div className="rd-disconnected__bottom">
            <Button variant="link" style={{ width: 360, height: 51 }} onClick={() => void logout()}>
              {t('confirmEmail.logout')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
